import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FingerPrintIcon } from "@heroicons/react/20/solid";
import {
    Button,
    Description,
    Divider,
    ErrorMessage,
    Field,
    FieldGroup,
    Form,
    Heading,
    Input,
    Label,
    RequiredLabel,
    Switch,
    SwitchField,
    Text,
    TextLink,
    notify,
} from "components";
import { PrimaryButton } from "src/components/primary-button";
import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "src/api/api";
import { OidcLoginFailure } from "src/api/generated";
import { AuthLayout } from "src/components/auth-layout";
import { InlineError } from "src/components/inline-error";
import { OpenIdLoginButtons } from "src/components/openid-login-buttons";
import { useAccount } from "src/context/account";
import { loadLastUsername, saveLastUsername } from "src/utils/username-storage";
import { handleFormError, isFormError } from "src/utils/error";
import { validateUsername } from "src/utils/username-rules";
import { authenticatePasskey, classifyPasskeyError } from "src/utils/webauthn";

/**
 * Search params of the login route
 */
export type LoginSearch = {
    /** Where to go once the login went through; set by {@link RequireAccount} */
    redirect?: string;
    /** Why an OIDC login was refused; set by the backend's OIDC callback */
    oidc_error?: OidcLoginFailure;
};

/**
 * Reads the `oidc_error` search param
 *
 * @param value the raw search param
 * @returns the failure it names, `failed` for one this frontend does not know, or `undefined` without one
 */
function parseOidcLoginFailure(value: unknown): OidcLoginFailure | undefined {
    if (value === undefined) return undefined;
    return Object.values(OidcLoginFailure).find((failure) => failure === value) ?? OidcLoginFailure.failed;
}

export const Route = createFileRoute("/_menu/auth/login")({
    component: RouteComponent,

    validateSearch: (search: Record<string, unknown>): LoginSearch => ({
        // Only same-site paths — an absolute url here would make this an open redirect.
        redirect:
            typeof search.redirect === "string" && search.redirect.startsWith("/") && !search.redirect.startsWith("//")
                ? search.redirect
                : undefined,
        oidc_error: parseOidcLoginFailure(search.oidc_error),
    }),
});

function RouteComponent() {
    const [t, i18n] = useTranslation("login");
    const navigate = useNavigate();
    const { redirect, oidc_error: oidcError } = Route.useSearch();
    const { refresh } = useAccount();

    // The lost-passkey request lives next to the form, not in it — submitting
    // the form is the login, this is the way out when that cannot work.
    const [recovery, setRecovery] = useState<"sent" | "failed" | "missing-username" | null>(null);
    const [recovering, setRecovering] = useState(false);

    const form = useForm({
        defaultValues: {
            username: loadLastUsername(),
            rememberMe: true,
        },
        validators: {
            onSubmitAsync: async ({ value: { username, rememberMe } }) => {
                // A name the backend's username rules reject cannot belong to any account,
                // and sending it anyway gets a plain 400 while the request body is parsed —
                // not a form error. Answer what a valid-but-unregistered name would get.
                if (validateUsername(username) !== null) {
                    return { fields: { username: t("error.unknown-username") }, form: undefined };
                }
                const started = await Api.auth.startLogin(username);
                if (isFormError(started)) {
                    return handleFormError(started.error, {
                        unknown_username: (errors) => {
                            errors.fields.username = t("error.unknown-username");
                        },
                        locked: (errors) => {
                            errors.form = t("error.locked");
                        },
                    });
                }

                let credential: unknown;
                try {
                    credential = await authenticatePasskey(started.options);
                } catch (error) {
                    console.error(error);
                    return handleFormError(classifyPasskeyError(error), {
                        unsupported: (errors) => {
                            errors.form = t("error.unsupported");
                        },
                        insecure_context: (errors) => {
                            errors.form = t("error.insecure-context");
                        },
                        wrong_domain: (errors) => {
                            errors.form = t("error.wrong-domain");
                        },
                        no_passkey_or_aborted: (errors) => {
                            errors.form = t("error.no-passkey-or-aborted");
                        },
                        // Registration-only, so it cannot happen here.
                        already_registered: (errors) => {
                            errors.form = t("error.unknown");
                        },
                        authenticator_error: (errors) => {
                            errors.form = t("error.authenticator-error");
                        },
                        unknown: (errors) => {
                            errors.form = t("error.unknown");
                        },
                    });
                }

                const finished = await Api.auth.finishLogin(credential, rememberMe);
                if (isFormError(finished)) {
                    return handleFormError(finished.error, {
                        no_ceremony: (errors) => {
                            errors.form = t("error.no-ceremony");
                        },
                        malformed_credential: (errors) => {
                            errors.form = t("error.rejected");
                        },
                        authentication_failed: (errors) => {
                            errors.form = t("error.rejected");
                        },
                        locked: (errors) => {
                            errors.form = t("error.locked");
                        },
                    });
                }

                // `login/finish` answers with no payload; the session read is the only source.
                const me = await refresh();
                if (me === null) {
                    return { form: t("error.rejected") };
                }
                saveLastUsername(me.username);
                notify.success(t("toast.logged-in", { username: me.username }));
                // Straight to `/home`, not to `/` — the index only exists to
                // redirect there, and going through it costs an extra navigation.
                await navigate({ to: redirect ?? "/home" });
            },
        },
    });

    /**
     * Requests a fresh registration link for the typed username.
     *
     * Reads the username straight out of the form — whoever lost a passkey has
     * usually already typed their name into the box above. The answer is the
     * same whether or not the account exists, so the note below only ever says
     * "if it exists, mail is on its way".
     */
    async function requestRecovery() {
        const username = form.state.values.username.trim();
        if (username === "") {
            setRecovery("missing-username");
            return;
        }

        setRecovering(true);
        setRecovery(null);
        try {
            // The mail should read like the page that asked for it.
            const language = i18n.resolvedLanguage?.startsWith("de") === true ? "De" : "En";
            await Api.auth.recover(username, language);
            setRecovery("sent");
        } catch (error) {
            // Most likely the rate limiter — either way a note, not the error screen.
            console.error(error);
            setRecovery("failed");
        }
        setRecovering(false);
    }

    const oidcErrors: Record<OidcLoginFailure, string> = {
        cancelled: t("error.oidc-cancelled"),
        expired: t("error.oidc-expired"),
        provider_unavailable: t("error.oidc-provider-unavailable"),
        email_unverified: t("error.oidc-email-unverified"),
        email_in_use: t("error.oidc-email-in-use"),
        locked: t("error.locked"),
        failed: t("error.oidc-failed"),
    };
    const oidcErrorMessage = oidcError === undefined ? undefined : oidcErrors[oidcError];

    const submitError = form.state.errorMap.onSubmit;
    const formError =
        typeof submitError === "object" && submitError !== null && "form" in submitError
            ? (submitError.form as string | undefined)
            : undefined;

    return (
        <AuthLayout>
            <div className={"flex flex-col gap-2 text-center"}>
                <Heading>{t("heading.login")}</Heading>
                <Text>{t("description.welcome-back")}</Text>
            </div>

            <Form onSubmit={form.handleSubmit}>
                <FieldGroup>
                    <form.Field name={"username"}>
                        {(fieldApi) => (
                            <Field>
                                <RequiredLabel>{t("label.username")}</RequiredLabel>
                                <Description>{t("description.login")}</Description>
                                <Input
                                    autoFocus={true}
                                    required={true}
                                    maxLength={32}
                                    autoComplete={"username webauthn"}
                                    invalid={fieldApi.state.meta.errors.length > 0}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((error) => (
                                    <ErrorMessage key={String(error)}>{String(error)}</ErrorMessage>
                                ))}
                                {formError !== undefined && <ErrorMessage>{formError}</ErrorMessage>}
                            </Field>
                        )}
                    </form.Field>

                    <form.Field name={"rememberMe"}>
                        {(fieldApi) => (
                            <SwitchField>
                                <Label>{t("label.remember-me")}</Label>
                                <Switch
                                    color={"lime"}
                                    checked={fieldApi.state.value}
                                    onChange={(checked) => fieldApi.handleChange(checked)}
                                />
                            </SwitchField>
                        )}
                    </form.Field>

                    <PrimaryButton type={"submit"} className={"w-full"} loading={form.state.isSubmitting}>
                        {!form.state.isSubmitting && <FingerPrintIcon data-slot={"icon"} aria-hidden={"true"} />}
                        {t("button.login")}
                    </PrimaryButton>

                    <div className={"flex flex-col items-center gap-2"}>
                        <Button plain={true} disabled={recovering} onClick={() => void requestRecovery()}>
                            {t("button.lost-passkey")}
                        </Button>
                        {recovery === "sent" && <Text className={"text-center"}>{t("description.recovery-sent")}</Text>}
                        {recovery === "missing-username" && (
                            <InlineError>{t("error.recovery-username-required")}</InlineError>
                        )}
                        {recovery === "failed" && <InlineError>{t("error.recovery-failed")}</InlineError>}
                    </div>
                </FieldGroup>
            </Form>

            <form.Subscribe selector={(state) => state.values.rememberMe}>
                {(rememberMe) => <OpenIdLoginButtons rememberMe={rememberMe} />}
            </form.Subscribe>

            {oidcErrorMessage !== undefined && <InlineError>{oidcErrorMessage}</InlineError>}

            <Divider soft={true} />

            <Text className={"text-center"}>
                {t("label.no-account")} <TextLink href={"/auth/signup"}>{t("button.signup-instead")}</TextLink>
            </Text>
        </AuthLayout>
    );
}
